/**
 * Axios client.
 *
 * Tokens live in memory (set via `setAccessToken`). The refresh token is in
 * an httpOnly cookie set by the backend, so we send `withCredentials: true`
 * on every request.
 *
 * If a 401 comes back, we try `/auth/refresh` once; if THAT fails, we wipe
 * the in-memory token and redirect to /login.
 */
import axios from "axios";

let accessToken = null;
let refreshInFlight = null;
export function setAccessToken(token) {
  accessToken = token || null;
}
export function getAccessToken() {
  return accessToken;
}

// Login-in-progress guard — prevents the 401 interceptor from wiping a
// freshly-issued token while the callback / SSO flow is still completing.
let _loginInProgress = false;
export function setLoginInProgress(v) { _loginInProgress = !!v; }

// Device-level preferences that are NOT tied to a user/session and must
// survive a logout (otherwise the UI flips theme on every sign-in).
const PRESERVE_LOCAL_KEYS = new Set(["tychi_theme"]);

/**
 * Forcefully wipe every trace of the current user's session from the
 * browser. Used on logout AND whenever the active user context changes so
 * a previous user's tokens / profile / role / fund_ids / org_id can never
 * leak into the next session.
 *
 * NOTE: the long-lived Cognito refresh token lives in an httpOnly cookie
 * scoped to `.tychi.co` — JS cannot read or delete it. That cookie is
 * revoked + cleared server-side by `POST /api/auth/logout`. This helper
 * handles everything the browser CAN reach.
 *
 * @param {{ preserveDeepLink?: boolean }} opts
 *   preserveDeepLink — keep the one-shot SSO deep-link keys
 *   (tychi_target_app / tychi_deep_fund_id) that an in-flight login relies
 *   on. Defaults to false (full wipe, used on logout).
 */
export function clearClientSession({ preserveDeepLink = false } = {}) {
  // 1. In-memory access / id token.
  setAccessToken(null);
  // 2. Abort any pending silent-refresh so it can't repopulate the token.
  refreshInFlight = null;

  // 3. localStorage — drop every auth/user/session key, keep device prefs.
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) keys.push(localStorage.key(i));
    keys.forEach((k) => {
      if (k && !PRESERVE_LOCAL_KEYS.has(k)) localStorage.removeItem(k);
    });
  } catch { /* storage unavailable */ }

  // 4. sessionStorage — fully session-scoped; clear it entirely unless an
  //    active login still needs its deep-link context.
  try {
    if (preserveDeepLink) {
      const keep = {
        tychi_target_app: sessionStorage.getItem("tychi_target_app"),
        tychi_deep_fund_id: sessionStorage.getItem("tychi_deep_fund_id"),
      };
      sessionStorage.clear();
      Object.entries(keep).forEach(([k, v]) => {
        if (v != null) sessionStorage.setItem(k, v);
      });
    } else {
      sessionStorage.clear();
    }
  } catch { /* storage unavailable */ }

  // 5. Best-effort: expire any JS-readable cookies on this host. (The
  //    httpOnly refresh cookie is intentionally untouched here — backend
  //    `/auth/logout` revokes + clears it.)
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (!name) return;
      const expire = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = expire;
      document.cookie = `${expire}; domain=${window.location.hostname}`;
    });
  } catch { /* ignore */ }
}

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 300000,
  withCredentials: true, // include the httpOnly refresh cookie
});

API.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

async function tryRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = API.post("/auth/refresh")
      .then((r) => {
        setAccessToken(r.data.access_token);
        return r.data.access_token;
      })
      .catch((err) => {
        setAccessToken(null);
        throw err;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    // Don't recurse on the refresh endpoint itself.
    const isAuthCall = original.url?.includes("/auth/refresh") || original.url?.includes("/auth/login-url") || original.url?.includes("/auth/callback") || original.url?.includes("/auth/sso-token") || original.url?.includes("/auth/logout");

    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        const newToken = await tryRefresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return API(original);
      } catch {
        // Refresh failed → session is dead.  BUT never wipe during an
        // active login/callback/SSO flow — completeLogin is about to
        // store a fresh token and clearClientSession would destroy it.
        const onAuthPage = window.location.pathname === "/callback" || window.location.pathname === "/login";
        if (!_loginInProgress && !onAuthPage) {
          clearClientSession();
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export default API;
